/**
 * TelemetryDebugInfo — component to display and copy telemetry session info for problem determination.
 *
 * Renders a small "Info Debug" button that opens a modal showing the current
 * session ID, URL, timestamp, and browser info. Inside the modal, a "Copia"
 * button copies the same info to the clipboard so the user can paste it into
 * a support ticket. The admin can then filter telemetry events by sessionId.
 *
 * Supports two modes:
 * - **Uncontrolled** (default): no props passed; the component manages its own
 *   modal open/close state and renders the "Info Debug" trigger button.
 * - **Controlled**: pass `externalOpen` and `onExternalClose` to let a parent
 *   (e.g. TopBar) drive the modal visibility. In this mode the internal
 *   trigger button is not rendered.
 *
 * @module TelemetryDebugInfo
 */

import { useCallback, useState } from 'react';
import { useTelemetry } from '@/lib/telemetry/TelemetryProvider';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';

/**
 * Detect browser name and version from userAgent.
 * Returns a human-readable string like "Chrome 121" or "Firefox 122".
 */
function detectBrowser(): string {
  const ua = navigator.userAgent;
  const browsers: { name: string; regex: RegExp }[] = [
    { name: 'Edge', regex: /Edg\/(\d+)/ },
    { name: 'Chrome', regex: /Chrome\/(\d+)/ },
    { name: 'Firefox', regex: /Firefox\/(\d+)/ },
    { name: 'Safari', regex: /Version\/(\d+).*Safari/ },
    { name: 'Opera', regex: /OPR\/(\d+)/ },
  ];
  for (const { name, regex } of browsers) {
    const match = ua.match(regex);
    if (match) return `${name} ${match[1]}`;
  }
  return ua;
}

/**
 * Detect OS name from userAgent.
 */
function detectOS(): string {
  const ua = navigator.userAgent;
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  if (/Android/.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  return 'Unknown';
}

/**
 * Build a formatted debug info string for clipboard copy.
 */
function buildDebugInfo(sessionId: string): string {
  const lines = [
    `Session ID: ${sessionId}`,
    `URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}`,
    `Timestamp: ${new Date().toISOString()}`,
    `Browser: ${detectBrowser()} / ${detectOS()}`,
  ];
  return lines.join('\n');
}

interface DebugRow {
  label: string;
  value: string;
  mono?: boolean;
}

/**
 * Build the list of debug info rows for display in the modal.
 */
function buildDebugRows(sessionId: string): DebugRow[] {
  return [
    { label: 'Session ID', value: sessionId, mono: true },
    { label: 'URL', value: typeof window !== 'undefined' ? window.location.href : 'N/A', mono: true },
    { label: 'Timestamp', value: new Date().toISOString(), mono: true },
    { label: 'Browser', value: `${detectBrowser()} / ${detectOS()}` },
  ];
}

interface TelemetryDebugInfoProps {
  /**
   * When provided (not undefined), the modal open/close state is controlled
   * by the parent. The component will not render its own trigger button.
   */
  externalOpen?: boolean;
  /**
   * Callback invoked when the modal should be closed by the parent.
   * Only used in controlled mode (when `externalOpen` is provided).
   */
  onExternalClose?: () => void;
}

export function TelemetryDebugInfo({ externalOpen, onExternalClose }: TelemetryDebugInfoProps = {}) {
  const { sessionId } = useTelemetry();
  const { success, error: toastError } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // --- Controlled vs uncontrolled mode ---
  const isControlled = externalOpen !== undefined;
  const modalOpen = isControlled ? externalOpen! : isModalOpen;
  const closeModal = isControlled
    ? () => { onExternalClose?.(); }
    : () => { setIsModalOpen(false); };

  const handleCopy = useCallback(async () => {
    const text = buildDebugInfo(sessionId);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      success('Info di debug copiate negli appunti');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Clipboard API might not be available (e.g. non-HTTPS context)
      // Fallback: create a temporary textarea and select it
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        success('Info di debug copiate negli appunti');
        setTimeout(() => setCopied(false), 3000);
      } catch {
        toastError('Impossibile copiare le info di debug');
      }
      document.body.removeChild(textarea);
    }
  }, [sessionId, success, toastError]);

  const debugRows = buildDebugRows(sessionId);

  return (
    <>
      {/* Render the internal trigger button only in uncontrolled mode */}
      {!isControlled && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsModalOpen(true)}
          title={`Session ID: ${sessionId}`}
        >
          Info Debug
        </Button>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title="Informazioni di Debug"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={closeModal}
            >
              Chiudi
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleCopy}
            >
              {copied ? '✓ Copiato!' : 'Copia info'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Usa queste informazioni per supporto tecnico. Copiale e incollale
            nella segnalazione: l&apos;amministratore potrà filtrare gli eventi
            di telemetria tramite il <strong>Session ID</strong>.
          </p>

          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {debugRows.map((row) => (
                  <tr key={row.label}>
                    <td className="px-4 py-2.5 font-medium text-gray-600 whitespace-nowrap align-top w-1/3">
                      {row.label}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-gray-900 ${
                        row.mono ? 'font-mono text-xs break-all' : ''
                      }`}
                    >
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
            <p className="text-xs text-blue-700">
              💡 Il <strong>Session ID</strong> identifica questa sessione di
              telemetria e permette all&apos;amministratore di isolare tutti gli
              eventi registrati durante la navigazione.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}